import type { SupabaseClient } from '../supabase/client';
import type {
  BarcodeErrorKey,
  BarcodeLookupInput,
  BarcodeLookupResult,
  BarcodeProductErrorKey,
  BarcodeStockUpdateInput,
  BarcodeStockUpdateResult,
} from '../types/barcode';
import type { VariantAvailabilityState } from '../types/product';
import { validateBarcode } from '../lib/barcode';

type BarcodeRpcRow = {
  result_status: string;
  normalized_barcode: string | null;
  variant_id: string | null;
  product_id: string | null;
  product_name: string | null;
  variant_option_summary: string | null;
  category_id: string | null;
  category_name: string | null;
  price_cents: number | null;
  sale_price_cents: number | null;
  currency: string | null;
  inventory: number | null;
  variant_active: boolean | null;
  product_published: boolean | null;
  availability_state: string | null;
  primary_image_path: string | null;
  barcode_value: string | null;
  deleted_at: string | null;
  product_deleted_at: string | null;
  variant_deleted_at: string | null;
};

function firstRow(data: unknown): BarcodeRpcRow | null {
  if (Array.isArray(data)) return (data[0] as BarcodeRpcRow | undefined) ?? null;
  return (data as BarcodeRpcRow | null) ?? null;
}

function asCurrency(value: string | null): 'ILS' {
  return value === 'ILS' ? 'ILS' : 'ILS';
}

function asAvailability(value: string | null): VariantAvailabilityState {
  if (
    value === 'available' ||
    value === 'low_stock' ||
    value === 'out_of_stock' ||
    value === 'disabled'
  ) {
    return value;
  }
  return 'available';
}

function requireString(value: string | null | undefined): string {
  return value ?? '';
}

function mapLookupRow(row: BarcodeRpcRow, fallbackNormalized: string | null): BarcodeLookupResult {
  const normalized = row.normalized_barcode ?? fallbackNormalized;

  if (row.result_status === 'invalid') {
    return {
      status: 'invalid',
      normalized,
      errorKey: 'admin.products.errors.barcodeInvalid',
    };
  }

  if (!normalized) {
    return {
      status: 'invalid',
      normalized: null,
      errorKey: 'admin.products.errors.barcodeInvalid',
    };
  }

  if (row.result_status === 'unknown') {
    return { status: 'unknown', normalized };
  }

  if (row.result_status === 'deleted_recoverable') {
    return {
      status: 'deleted_recoverable',
      normalized,
      deleted: {
        variantId: requireString(row.variant_id),
        productId: requireString(row.product_id),
        productName: requireString(row.product_name),
        variantOptionSummary: requireString(row.variant_option_summary),
        deletedAt: requireString(row.deleted_at),
        productDeletedAt: row.product_deleted_at,
        variantDeletedAt: row.variant_deleted_at,
        barcodeValue: requireString(row.barcode_value),
        barcodeNormalized: normalized,
      },
    };
  }

  return {
    status: 'known',
    normalized,
    variant: {
      variantId: requireString(row.variant_id),
      productId: requireString(row.product_id),
      productName: requireString(row.product_name),
      variantOptionSummary: requireString(row.variant_option_summary),
      categoryId: row.category_id,
      categoryName: row.category_name,
      priceCents: row.price_cents ?? 0,
      salePriceCents: row.sale_price_cents,
      currency: asCurrency(row.currency),
      inventory: row.inventory ?? 0,
      active: row.variant_active === true,
      productPublished: row.product_published === true,
      availabilityState: asAvailability(row.availability_state),
      primaryImagePath: row.primary_image_path,
      barcodeValue: requireString(row.barcode_value),
      barcodeNormalized: normalized,
    },
  };
}

export async function findVariantByBarcode(
  client: SupabaseClient,
  input: BarcodeLookupInput,
  options?: { signal?: AbortSignal },
): Promise<BarcodeLookupResult> {
  const validation = validateBarcode(input.rawBarcode);
  if (!validation.valid) {
    return {
      status: 'invalid',
      normalized: validation.normalized,
      errorKey: validation.errorKey ?? 'admin.products.errors.barcodeInvalid',
    };
  }

  let request = client.rpc('find_variant_by_barcode', {
    target_store_id: input.storeId,
    raw_barcode: input.rawBarcode,
  });
  if (options?.signal) {
    request = request.abortSignal(options.signal);
  }

  const { data, error } = await request;
  if (error) throw error;

  const row = firstRow(data);
  if (!row) return { status: 'unknown', normalized: validation.normalized ?? '' };

  return mapLookupRow(row, validation.normalized);
}

export async function receiveVariantStock(
  client: SupabaseClient,
  input: BarcodeStockUpdateInput,
): Promise<BarcodeStockUpdateResult> {
  const { data, error } = await client.rpc('receive_variant_stock', {
    target_variant_id: input.variantId,
    update_mode: input.mode,
    quantity: input.quantity,
  });
  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw Object.assign(new Error('stock update failed'), { code: 'P0002' });
  const mapped = row as {
    variant_id: string;
    product_id: string;
    inventory_before: number;
    inventory_after: number;
  };
  return {
    variantId: mapped.variant_id,
    productId: mapped.product_id,
    inventoryBefore: mapped.inventory_before,
    inventoryAfter: mapped.inventory_after,
  };
}

export function mapBarcodeError(err: unknown): BarcodeErrorKey {
  if (err instanceof TypeError || (err instanceof Error && /fetch/i.test(err.message))) {
    return 'admin.barcode.errors.networkFailed';
  }

  const maybe = err as { code?: string; status?: number; name?: string; message?: string };
  if (maybe?.name === 'AuthRetryableFetchError') return 'admin.barcode.errors.networkFailed';
  if (maybe?.code === '42501' || maybe?.status === 401 || maybe?.status === 403) {
    return 'admin.barcode.errors.permissionDenied';
  }
  if (maybe?.code === 'P0451') return 'admin.products.errors.barcodeInvalid';
  if (maybe?.code === 'P0456') return 'admin.products.errors.barcodeInvalid';
  if (
    maybe?.code === '22023' &&
    /inventory quantity exceeds maximum|quantity exceeds maximum/i.test(maybe.message ?? '')
  ) {
    return 'admin.barcode.stock.errors.maxQuantity';
  }
  return 'admin.barcode.errors.unknown';
}

export function mapBarcodeProductError(err: unknown): BarcodeProductErrorKey {
  const maybe = err as { code?: string; constraint?: string; message?: string };
  if (maybe?.code === 'P0450') return 'admin.products.errors.barcodeDuplicate';
  if (maybe?.code === 'P0451') return 'admin.products.errors.barcodeInvalid';
  if (maybe?.code === 'P0452') return 'admin.products.errors.barcodeDeletedConflict';
  if (maybe?.code === 'P0453') {
    return 'admin.products.errors.barcodeReplaceConfirmationRequired';
  }
  if (maybe?.code === 'P0454') return 'admin.products.errors.barcodeClearConfirmationRequired';
  if (maybe?.code === 'P0455') return 'admin.products.errors.barcodeChangedElsewhere';
  if (
    maybe?.code === '23505' &&
    (maybe.constraint === 'product_variants_barcode_live_unique_idx' ||
      maybe.message?.includes('product_variants_barcode_live_unique_idx'))
  ) {
    return 'admin.products.errors.barcodeDuplicate';
  }
  return 'common.errors.unknown';
}
